import { motion } from "framer-motion";
import { useMemo, useState } from "react";
import {
  FiArrowRight,
  FiCheckCircle,
  FiGrid,
  FiList,
  FiSearch,
} from "react-icons/fi";
import { Link } from "react-router-dom";
import { categories, getCategoryTopSites } from "../../data/fakeData";
import StarRating from "../UI/StarRating";

const CategoryPage = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("count");
  const [viewMode, setViewMode] = useState("grid");

  const filtered = useMemo(() => {
    let result = [...categories];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.description.toLowerCase().includes(q),
      );
    }
    switch (sortBy) {
      case "count":
        result.sort((a, b) => b.count - a.count);
        break;
      case "name":
        result.sort((a, b) => a.name.localeCompare(b.name));
        break;
      default:
        break;
    }
    return result;
  }, [searchQuery, sortBy]);

  return (
    <div className="section-tinted min-h-screen">
      <div className="container-main py-10 lg:py-14">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="accent-line" />
          <p className="label-tag mb-2">All Topics</p>
          <h1 className="page-title">Browse Categories</h1>
          <p className="text-base text-slate-500 mt-2 max-w-xl leading-relaxed">
            Explore{" "}
            <span className="font-bold text-slate-800">
              {categories.length} categories
            </span>{" "}
            of curated websites, each featuring community-rated top picks.
          </p>
        </motion.div>

        {/* Controls */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 mb-6 pb-5 border-b-2 border-slate-100"
        >
          <div className="relative flex-1 max-w-md">
            <FiSearch
              size={15}
              className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search categories..."
              className="w-full bg-white border-2 border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-sm text-slate-900 placeholder-slate-400 font-medium focus:outline-none focus:border-indigo-400 transition-all"
            />
          </div>
          <div className="flex items-center gap-2">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="select-field"
            >
              <option value="count">Most Websites</option>
              <option value="name">Alphabetical</option>
            </select>
            <div className="flex border-2 border-slate-200 rounded-xl overflow-hidden bg-white">
              <button
                onClick={() => setViewMode("grid")}
                className={`p-2.5 transition-colors ${viewMode === "grid" ? "bg-indigo-50 text-indigo-600" : "text-slate-400 hover:text-slate-600"}`}
              >
                <FiGrid size={15} />
              </button>
              <button
                onClick={() => setViewMode("list")}
                className={`p-2.5 transition-colors ${viewMode === "list" ? "bg-indigo-50 text-indigo-600" : "text-slate-400 hover:text-slate-600"}`}
              >
                <FiList size={15} />
              </button>
            </div>
            <span className="text-sm font-bold text-slate-500 ml-1">
              {filtered.length} results
            </span>
          </div>
        </motion.div>

        {/* No results */}
        {filtered.length === 0 && (
          <div className="card p-12 text-center">
            <h3 className="text-lg font-bold text-slate-800 mb-2">
              No categories found
            </h3>
            <p className="text-slate-500">Try a different search term.</p>
          </div>
        )}

        {/* Grid View */}
        {viewMode === "grid" && filtered.length > 0 && (
          <div className="grid md:grid-cols-2 gap-5">
            {filtered.map((cat, i) => {
              const tops = getCategoryTopSites(cat.slug, 3);
              return (
                <motion.div
                  key={cat.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.04 }}
                  className="card-hover overflow-hidden"
                >
                  {/* Photo header */}
                  <div className="relative h-32 overflow-hidden">
                    <img
                      src={cat.photo}
                      alt={cat.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                    <div className="absolute inset-0 bg-gradient-to-r from-black/20 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-4 flex items-end justify-between">
                      <div>
                        <h2 className="font-bold text-white text-lg leading-tight">
                          {cat.name}
                        </h2>
                        <p className="text-xs text-white/70 font-semibold mt-0.5">
                          {cat.count} websites
                        </p>
                      </div>
                      <Link
                        to={`/category/${cat.slug}`}
                        onClick={(e) => e.stopPropagation()}
                        className="flex items-center gap-1 text-xs font-bold text-white bg-white/20 backdrop-blur-sm hover:bg-white/30 px-3 py-1.5 rounded-lg transition-all"
                      >
                        View all <FiArrowRight size={11} />
                      </Link>
                    </div>
                    {/* Color accent strip */}
                    <div
                      className="absolute top-0 left-0 right-0 h-1"
                      style={{ backgroundColor: cat.color }}
                    />
                  </div>

                  {/* Description */}
                  <div className="px-5 py-4 border-b border-slate-100">
                    <p className="text-sm text-slate-600 leading-relaxed">
                      {cat.description}
                    </p>
                  </div>

                  {/* Top picks */}
                  {tops.length > 0 && (
                    <div className="px-5 pb-4 pt-3 bg-slate-50/50">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-3">
                        Top Rated
                      </p>
                      <div className="space-y-2">
                        {tops.map((site, j) => (
                          <Link
                            key={site.id}
                            to={`/website/${site.id}`}
                            className="flex items-center gap-3 p-2 rounded-xl hover:bg-white transition-colors group"
                          >
                            <span className="text-[10px] font-extrabold text-slate-300 w-4">
                              {j + 1}
                            </span>
                            <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0 ring-1 ring-slate-200">
                              <img
                                src={site.thumbnail}
                                alt={site.name}
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                <span className="text-sm font-bold text-slate-700 group-hover:text-indigo-600 truncate transition-colors">
                                  {site.name}
                                </span>
                                {site.verified && (
                                  <FiCheckCircle
                                    size={11}
                                    className="text-indigo-500 flex-shrink-0"
                                  />
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <StarRating rating={site.rating} size="xs" />
                              <span className="text-xs font-extrabold text-slate-700">
                                {site.rating}
                              </span>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}

        {/* List View */}
        {viewMode === "list" && filtered.length > 0 && (
          <div className="card overflow-hidden shadow-lg shadow-slate-200/40">
            {filtered.map((cat, i) => (
              <motion.div
                key={cat.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: i * 0.03 }}
              >
                <Link
                  to={`/category/${cat.slug}`}
                  className="flex items-center gap-4 px-5 py-4 border-b border-slate-100 last:border-0 hover:bg-indigo-50/40 transition-colors group"
                >
                  {/* Photo thumbnail */}
                  <div className="w-16 h-12 rounded-xl overflow-hidden flex-shrink-0 ring-2 ring-slate-100">
                    <img
                      src={cat.photo}
                      alt={cat.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>

                  {/* Color dot */}
                  <div
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: cat.color }}
                  />

                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-bold text-slate-900 group-hover:text-indigo-600 transition-colors">
                      {cat.name}
                    </h3>
                    <p className="text-xs text-slate-500 truncate mt-0.5">
                      {cat.description}
                    </p>
                  </div>
                  <span className="text-sm font-extrabold text-slate-700">
                    {cat.count}
                  </span>
                  <span className="badge-slate text-[10px] hidden sm:inline-flex">
                    websites
                  </span>
                  <FiArrowRight
                    size={14}
                    className="text-slate-300 group-hover:text-indigo-500 transition-colors flex-shrink-0"
                  />
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryPage;
